import { useCallback, useEffect, useState } from 'react';
import { api, formatRelative, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, Modal, Field, Input, Select, Alert, Spinner, Empty } from '../components/ui';

interface ApiKey {
  id: string; name: string; prefix: string; state: 'active' | 'expired' | 'revoked';
  createdAt: string; expiresAt: string; revokedAt: string | null;
  lastUsedAt: string | null; lastUsedIp: string | null;
  userId: string; ownerName?: string; ownerEmail?: string;
}

const LIFETIME_LABEL: Record<number, string> = { 30: '30 days', 90: '90 days', 180: '6 months', 365: '1 year' };

// SQLite datetimes come back without a zone; they are UTC.
const asDate = (iso: string) => new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
const formatDay = (iso: string) => asDate(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * API keys: how a script, or another Claude session, calls the hub without a
 * browser. A key is its owner - it can do what they can and no more - so
 * everyone makes their own here. Administrators also see everyone else's, to
 * revoke one that should not be out there.
 */
export function ApiKeys() {
  const { user } = useAuth();
  const [mine, setMine] = useState<ApiKey[] | null>(null);
  const [everyone, setEveryone] = useState<ApiKey[]>([]);
  const [lifetimes, setLifetimes] = useState<number[]>([30, 90, 180, 365]);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', days: 90 });
  const [busy, setBusy] = useState(false);
  // The key just made. It lives only here: once this is cleared it is gone.
  const [made, setMade] = useState<{ name: string; key: string } | null>(null);

  const isAdmin = user?.isAdmin ?? false;

  const load = useCallback(async () => {
    try {
      const own = await api.get<{ keys: ApiKey[]; lifetimes: number[]; defaultLifetime: number }>('/keys');
      setMine(own.keys);
      setLifetimes(own.lifetimes);
      if (isAdmin) setEveryone((await api.get<{ keys: ApiKey[] }>('/keys/all')).keys);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the keys.');
      setMine((m) => m ?? []);
    }
  }, [isAdmin]);

  useEffect(() => { void load(); }, [load]);

  async function create() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ key: string }>('/keys', { name: form.name.trim(), days: form.days });
      setMade({ name: form.name.trim(), key: r.key });
      setCreating(false);
      setForm({ name: '', days: 90 });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not make the key.');
      setCreating(false);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: ApiKey) {
    const whose = k.userId === user?.id ? 'your' : `${k.ownerName ?? 'this user'}'s`;
    if (!window.confirm(`Revoke ${whose} key "${k.name}" (${k.prefix}…)?\n\nAnything still using it stops working at once. This cannot be undone.`)) return;
    setError(null);
    try {
      await api.delete(`/keys/${k.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke the key.');
    }
  }

  if (!mine) return <Spinner />;

  const others = everyone.filter((k) => k.userId !== user?.id);

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <h1 className="text-lg font-semibold">API keys</h1>
        <p className="text-sm text-ink-muted">
          For scripts and tools that call the hub without a browser. A key acts as you: it can do
          what you can do, and stops working the moment your account is disabled.
        </p>
      </div>

      {error && <Alert tone="critical">{error}</Alert>}

      {/* Shown once. There is deliberately no way to retrieve it later. */}
      {made && (
        <Alert tone="good" title={`Key "${made.name}" made`}>
          <div className="mt-2 space-y-3">
            <div>Copy it now. It is shown once and cannot be recovered - if it is lost, revoke it and make another.</div>
            <Copyable value={made.key} label="API key" />
            <div>
              <div className="mb-1 text-xs text-ink-muted">Try it:</div>
              <Copyable
                value={`curl -s -H "Authorization: Bearer ${made.key}" ${window.location.origin}/api/instances`}
                label="curl example"
              />
            </div>
            <Button variant="ghost" onClick={() => setMade(null)}>I have copied it</Button>
          </div>
        </Alert>
      )}

      <Card
        title="Your keys"
        action={<Button variant="primary" onClick={() => { setError(null); setCreating(true); }}>New key</Button>}
      >
        {mine.length === 0 ? (
          <Empty title="No keys yet">Make one for each script or tool, so each can be revoked by itself.</Empty>
        ) : (
          <KeyTable keys={mine} onRevoke={revoke} />
        )}
      </Card>

      {isAdmin && (
        <Card title="Everyone else's keys">
          {others.length === 0 ? (
            <Empty title="Nobody else has made a key" />
          ) : (
            <KeyTable keys={others} onRevoke={revoke} showOwner />
          )}
        </Card>
      )}

      <Alert tone="info">
        Send it as <code className="font-mono text-xs">Authorization: Bearer cph_…</code> on any request to{' '}
        <code className="font-mono text-xs">{window.location.origin}/api</code>. A key cannot make or revoke keys,
        manage users, roles or access, or change your password or two-factor - those need you, signed in here.
      </Alert>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New API key"
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button variant="primary" loading={busy} disabled={!form.name.trim()} onClick={() => void create()}>
              Make key
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name" hint="What will use it - for example “Claude on my laptop” or “nightly cost report”.">
            <Input value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </Field>
          <Field label="Lasts" hint="After this it stops working and you make a new one.">
            <Select value={form.days} onChange={(e) => setForm({ ...form, days: Number(e.target.value) })}>
              {lifetimes.map((d) => <option key={d} value={d}>{LIFETIME_LABEL[d] ?? `${d} days`}</option>)}
            </Select>
          </Field>
          <Alert tone="info">The key is shown once, straight after you make it.</Alert>
        </div>
      </Modal>
    </div>
  );
}

function KeyTable({ keys, onRevoke, showOwner = false }: { keys: ApiKey[]; onRevoke: (k: ApiKey) => void; showOwner?: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-grid text-left text-xs text-ink-muted">
            <th className="px-4 py-2 font-medium">Name</th>
            {showOwner && <th className="px-4 py-2 font-medium">Owner</th>}
            <th className="px-4 py-2 font-medium">Key</th>
            <th className="px-4 py-2 font-medium">Created</th>
            <th className="px-4 py-2 font-medium">Expires</th>
            <th className="px-4 py-2 font-medium">Last used</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id} className={`border-b border-grid last:border-0 ${k.state === 'active' ? '' : 'text-ink-muted'}`}>
              <td className="px-4 py-2.5 font-medium">{k.name}</td>
              {showOwner && (
                <td className="px-4 py-2.5">
                  <div>{k.ownerName}</div>
                  <div className="text-xs text-ink-muted">{k.ownerEmail}</div>
                </td>
              )}
              <td className="px-4 py-2.5 font-mono text-xs">{k.prefix}…</td>
              <td className="px-4 py-2.5 text-xs text-ink-muted">{formatDay(k.createdAt)}</td>
              <td className="px-4 py-2.5 text-xs">
                {k.state === 'revoked'
                  ? `revoked ${formatRelative(k.revokedAt)}`
                  : k.state === 'expired'
                    ? `expired ${formatDay(k.expiresAt)}`
                    : formatDay(k.expiresAt)}
              </td>
              <td className="px-4 py-2.5 text-xs text-ink-muted" title={k.lastUsedIp ? `from ${k.lastUsedIp}` : undefined}>
                {formatRelative(k.lastUsedAt)}
              </td>
              <td className="px-4 py-2.5 text-right">
                {k.state === 'active' && <Button variant="ghost" onClick={() => onRevoke(k)}>Revoke</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A value in full with a copy button. Not masked: it is on screen once, to be copied. */
function Copyable({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-start gap-2">
      <code className="min-w-0 flex-1 break-all rounded bg-plane px-2 py-1.5 font-mono text-xs text-ink">{value}</code>
      <Button
        variant="ghost"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        aria-label={`Copy ${label}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}
